import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Modal, Form, Input, Select, DatePicker, Button, message, Tooltip } from 'antd';
import { UploadOutlined, CloseOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { getSignedUrl } from 'store/commonSlice';
import { OWNERSHIP_TYPE_OPTIONS } from 'componentsv2/forms/vendor-steps/VehicleInformationStep.constants';
import {
	SkipDocumentUploadRow,
	SkipDocumentCheckbox,
	SkipDocumentInfoIcon,
} from './CreateVehiclePageV2.styles';
import {
	VALID_TILL_DATE_MESSAGE,
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from './CreateVehiclePageV2.constants';
import { formatFileSize } from './CreateVehiclePageV2.utils';
import DocumentViewerModal from 'componentsv2/common/modals/DocumentViewerModal';

const { Option } = Select;

// Styled components
const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 12px;
	}

	.ant-modal-header {
		border-bottom: 1px solid #e5e7eb;
		padding: 20px 24px;
	}

	.ant-modal-title {
		font-size: 18px;
		font-weight: 600;
		color: #0f172a;
	}

	.ant-modal-body {
		padding: 24px;
	}

	.ant-modal-footer {
		border-top: 1px solid #e5e7eb;
		padding: 16px 24px;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item-label > label {
		font-weight: 500;
		font-size: 14px;
		color: #1e293b;
	}

	.ant-form-item {
		margin-bottom: 16px;
	}

	.ant-input,
	.ant-select-selector,
	.ant-picker {
		border-radius: 6px;
		border: 1px solid #e2e8f0;
		height: 40px !important;
	}

	.ant-select-selector {
		height: 40px !important;
		padding: 4px 11px !important;
		display: flex !important;
		align-items: center !important;
	}

	.ant-picker {
		width: 100%;
		padding: 4px 11px !important;
	}
`;

const FormGrid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 24px;
	row-gap: 16px;
`;

const InlineRow = styled(Box)`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: 100%;
`;

const UploadButtonWrapper = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 6px;
	flex-shrink: 0;
`;

const DatePickerWrapper = styled(Box)`
	flex: 1;
	min-width: 0;
`;

const PrimaryBrowseFileButton = styled(Button)`
	height: 40px;
	padding: 0 16px;
	border: none;
	color: white;
	background-color: #1e3a8a;
	border-radius: 6px;
	font-weight: 500;

	&:hover:not(:disabled) {
		background-color: #1e40af !important;
		color: white !important;
	}
`;

const HiddenFileInput = styled.input`
	display: none !important;
	visibility: hidden !important;
	position: absolute !important;
	width: 0 !important;
	height: 0 !important;
	opacity: 0 !important;
	pointer-events: none !important;
`;

const FileDisplayContainer = styled(Flex)`
	margin-top: 8px;
	padding: 8px 12px;
	background-color: #f9fafb;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

const FileInfo = styled(Flex)`
	align-items: center;
	gap: 8px;
	flex: 1;
	min-width: 0;
`;

const FileIcon = styled(FileTextOutlined)`
	font-size: 18px;
	color: #dc2626;
	flex-shrink: 0;
`;

const RemoveFileButton = styled(Box)`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 4px;
	color: #64748b;
	transition: all 0.2s;

	&:hover {
		background-color: #e2e8f0;
		color: #dc2626;
	}
`;

const FooterActions = styled(Flex)`
	justify-content: space-between;
	width: 100%;
`;

const RemoveButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	border: 1px solid #dc2626;
	color: #dc2626;
	background: white;
	border-radius: 6px;

	&:hover {
		border-color: #b91c1c !important;
		color: #b91c1c !important;
		background: #fef2f2 !important;
	}
`;

const ConfirmButton = styled(Button)`
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	color: white;
	border-radius: 6px;

	&:hover {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
	}
`;

function EditVehicleModal({
	visible,
	vehicle,
	vendorOptions,
	vehicleTypeOptions,
	onClose,
	onUpdate,
	onRemove,
}) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [skipDocumentUpload, setSkipDocumentUpload] = useState(false);
	const [isSkipDocumentUploadLocked, setIsSkipDocumentUploadLocked] = useState(false);

	// File upload refs
	const rcDocFileInputRef = useRef(null);
	const insuranceDocFileInputRef = useRef(null);
	const pucDocFileInputRef = useRef(null);
	const consentFileInputRef = useRef(null);

	// File upload states
	const [uploading, setUploading] = useState({
		rc_doc: false,
		insurance: false,
		puc_doc: false,
		consent_letter: false,
	});

	// File keys
	const [fileKeys, setFileKeys] = useState({
		rc_doc_key: null,
		insurance_key: null,
		puc_doc_key: null,
		consent_letter_key: null,
	});

	// File names for display
	const [fileNames, setFileNames] = useState({
		rc_doc: null,
		insurance: null,
		puc_doc: null,
		consent_letter: null,
	});
	const isPucExpiryDateRequired = !skipDocumentUpload && Boolean(fileKeys.puc_doc_key);

	// File sizes for display
	const [fileSizes, setFileSizes] = useState({
		rc_doc: null,
		insurance: null,
		puc_doc: null,
		consent_letter: null,
	});

	// Document viewer modal state
	const [documentViewerVisible, setDocumentViewerVisible] = useState(false);
	const [viewingDocumentUrl, setViewingDocumentUrl] = useState('');
	const [viewingDocumentName, setViewingDocumentName] = useState('');
	const [viewingDocumentType, setViewingDocumentType] = useState('');

	// Watch ownership type for consent letter button
	const ownershipType = Form.useWatch('ownership_type', form);
	const gpsInstalled = Form.useWatch('gps_installed', form);

	// Helper function to extract filename from key
	const extractFileName = (key) => (key ? key.split('/').pop() : null);

	// Helper function to initialize form data
	const initializeFormData = (vehicleData) => {
		const hasUploadedMandatoryDocuments = Boolean(
			vehicleData.rc_document_key && vehicleData.insurance_document_key
		);
		const isSavedVehicle = Boolean(vehicleData.id || vehicleData.vehicle_id);
		const shouldLockSkipDocumentUpload = isSavedVehicle && hasUploadedMandatoryDocuments;
		setIsSkipDocumentUploadLocked(shouldLockSkipDocumentUpload);
		setSkipDocumentUpload(!!vehicleData.skip_document_upload && !shouldLockSkipDocumentUpload);
		form.setFieldsValue({
			vendor_id: vehicleData.vendor_id,
			vehicle_number: vehicleData.vehicle_number,
			vehicle_type_id: vehicleData.vehicle_type_id,
			rc_doc_key: vehicleData.rc_document_key,
			rc_expiry_date: vehicleData.rc_expiry_date ? dayjs(vehicleData.rc_expiry_date) : null,
			insurance_key: vehicleData.insurance_document_key,
			insurance_validity_date: vehicleData.insurance_validity_date
				? dayjs(vehicleData.insurance_validity_date)
				: null,
			puc_doc_key: vehicleData.puc_document_key,
			puc_expiry_date: vehicleData.puc_expiry_date
				? dayjs(vehicleData.puc_expiry_date)
				: null,
			consent_letter_key: vehicleData.consent_letter_key,
			ownership_type: vehicleData.ownership_type,
			gps_installed: vehicleData.gps_installed,
			gps_provider_name: vehicleData.gps_provider_name,
		});

		setFileKeys({
			rc_doc_key: vehicleData.rc_document_key,
			insurance_key: vehicleData.insurance_document_key,
			puc_doc_key: vehicleData.puc_document_key,
			consent_letter_key: vehicleData.consent_letter_key,
		});

		setFileNames({
			rc_doc: extractFileName(vehicleData.rc_document_key),
			insurance: extractFileName(vehicleData.insurance_document_key),
			puc_doc: extractFileName(vehicleData.puc_document_key),
			consent_letter: extractFileName(vehicleData.consent_letter_key),
		});
	};

	// Initialize form with vehicle data
	useEffect(() => {
		if (visible && vehicle) {
			initializeFormData(vehicle);
		}
	}, [visible, vehicle, form]);

	// Handle file upload
	const handleFileUpload = async (file, fieldName) => {
		if (!file) {
			message.error('Please select a file');
			return false;
		}

		const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
		const maxSizeMB = 5;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, PNG, JPG, JPEG files!');
			return false;
		}
		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		setFileNames((prev) => ({ ...prev, [fieldName]: file.name }));
		setFileSizes((prev) => ({ ...prev, [fieldName]: file.size }));
		setUploading((prev) => ({ ...prev, [fieldName]: true }));

		try {
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			const keyField = `${fieldName}_key`;
			setFileKeys((prev) => ({ ...prev, [keyField]: fileData.filename }));
			form.setFieldsValue({ [keyField]: fileData.filename });

			message.success('File uploaded successfully');
			return true;
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload file');
			setFileNames((prev) => ({ ...prev, [fieldName]: null }));
			setFileSizes((prev) => ({ ...prev, [fieldName]: null }));
			return false;
		} finally {
			setUploading((prev) => ({ ...prev, [fieldName]: false }));
		}
	};

	// Handle file input change
	const handleFileChange = async (event, fieldName) => {
		const file = event.target.files?.[0];
		if (file) {
			await handleFileUpload(file, fieldName);
		}
		if (event.target) {
			event.target.value = '';
		}
	};

	// Handle browse file click
	const handleBrowseFile = (fieldName, ref) => {
		ref.current?.click();
	};

	// Handle file removal
	const handleRemoveFile = (fieldName) => {
		const keyField = `${fieldName}_key`;
		setFileKeys((prev) => ({ ...prev, [keyField]: null }));
		setFileNames((prev) => ({ ...prev, [fieldName]: null }));
		setFileSizes((prev) => ({ ...prev, [fieldName]: null }));
		form.setFieldsValue({ [keyField]: null });
		message.success('File removed');
	};

	const clearUploadedDocumentState = () => {
		setFileKeys({
			rc_doc_key: null,
			insurance_key: null,
			puc_doc_key: null,
			consent_letter_key: null,
		});
		setFileNames({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		setFileSizes({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		form.setFieldsValue({
			rc_doc_key: null,
			rc_expiry_date: null,
			insurance_key: null,
			insurance_validity_date: null,
			puc_doc_key: null,
			puc_expiry_date: null,
			consent_letter_key: null,
			ownership_type: null,
			gps_installed: null,
			gps_provider_name: null,
		});
	};

	// Handle viewing document
	const handleViewDocument = async (documentKey, documentName = 'Document') => {
		if (!documentKey) {
			message.error('No document key provided');
			return;
		}

		try {
			// Get signed URL for viewing
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor',
						keys: [documentKey],
					},
				})
			);

			if (response && response.status === 'success' && response.data?.[0]?.url) {
				const documentUrl = response.data[0].url;

				// Determine document type from URL or key
				let docType = 'unknown';
				const lowerKey = documentKey.toLowerCase();
				if (lowerKey.includes('.pdf')) {
					docType = 'pdf';
				} else if (
					lowerKey.includes('.jpg') ||
					lowerKey.includes('.jpeg') ||
					lowerKey.includes('.png')
				) {
					docType = 'image';
				}

				setViewingDocumentUrl(documentUrl);
				setViewingDocumentName(documentName);
				setViewingDocumentType(docType);
				setDocumentViewerVisible(true);
			} else {
				message.error('Failed to load document - Invalid response');
			}
		} catch (error) {
			console.error('Error loading document:', error);
			message.error(`Failed to load document: ${error.message}`);
		}
	};

	// Handle closing document viewer
	const handleCloseDocumentViewer = () => {
		setDocumentViewerVisible(false);
		setViewingDocumentUrl('');
		setViewingDocumentName('');
		setViewingDocumentType('');
	};

	// Handle confirm (update)
	const handleConfirm = () => {
		form.validateFields()
			.then((values) => {
				const updatedVehicle = {
					...vehicle,
					vendor_id: values.vendor_id,
					vendor_name:
						vendorOptions.find((v) => v.value === values.vendor_id)?.label || '',
					vehicle_type_id: values.vehicle_type_id,
					vehicle_type_name:
						vehicleTypeOptions.find((v) => v.value === values.vehicle_type_id)?.label ||
						'',
					vehicle_number: values.vehicle_number,
					rc_document_key: fileKeys.rc_doc_key,
					rc_expiry_date: values.rc_expiry_date,
					insurance_document_key: fileKeys.insurance_key,
					insurance_validity_date: values.insurance_validity_date,
					puc_document_key: fileKeys.puc_doc_key,
					puc_expiry_date: values.puc_expiry_date,
					consent_letter_key: fileKeys.consent_letter_key,
					ownership_type: values.ownership_type || null,
					gps_installed: values.gps_installed,
					gps_provider_name: values.gps_provider_name,
					skip_document_upload: skipDocumentUpload && !isSkipDocumentUploadLocked,
				};

				onUpdate(updatedVehicle);
				handleClose();
			})
			.catch((error) => {
				console.error('Validation failed:', error);
			});
	};

	// Handle remove vehicle
	const handleRemove = () => {
		Modal.confirm({
			title: 'Remove Vehicle',
			content: 'Are you sure you want to remove this vehicle from the list?',
			okText: 'Remove',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: () => {
				onRemove(vehicle);
				handleClose();
			},
		});
	};

	// Handle modal close
	const handleClose = () => {
		form.resetFields();
		setSkipDocumentUpload(false);
		setIsSkipDocumentUploadLocked(false);
		setFileKeys({
			rc_doc_key: null,
			insurance_key: null,
			puc_doc_key: null,
			consent_letter_key: null,
		});
		setFileNames({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		setFileSizes({
			rc_doc: null,
			insurance: null,
			puc_doc: null,
			consent_letter: null,
		});
		onClose();
	};

	return (
		<>
			<StyledModal
				title="Edit Details"
				open={visible}
				onCancel={handleClose}
				width={900}
				footer={
					<FooterActions>
						<RemoveButton onClick={handleRemove}>Remove Vehicle</RemoveButton>
						<ConfirmButton onClick={handleConfirm}>Confirm</ConfirmButton>
					</FooterActions>
				}
			>
				<StyledForm form={form} layout="vertical">
					<FormGrid>
						{/* Row 1: Select Vendor | Vehicle Number */}
						<Form.Item
							name="vendor_id"
							label="Select Vendor"
							rules={[{ required: true, message: 'Please select vendor' }]}
						>
							<Select
								placeholder="Select"
								showSearch
								filterOption={(input, option) =>
									(option?.children ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{vendorOptions.map((vendor) => (
									<Option key={vendor.value} value={vendor.value}>
										{vendor.label}
									</Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							name="vehicle_number"
							label="Vehicle Number"
							rules={[
								{ required: true, message: 'Please enter vehicle number' },
								{
									pattern: VEHICLE_NUMBER_PATTERN,
									message: VEHICLE_NUMBER_MESSAGE,
								},
								{
									transform: (value) => value?.toUpperCase(),
								},
							]}
							normalize={(value) => value?.toUpperCase()}
						>
							<Input
								placeholder="Eg : DL01AB1234 or DL 01 AB 1234"
								style={{ textTransform: 'uppercase' }}
							/>
						</Form.Item>

						{/* Row 2: Vehicle Type | Insurance Doc Upload + Valid Till */}
						<Form.Item
							name="vehicle_type_id"
							label="Vehicle Type"
							rules={[{ required: true, message: 'Please select vehicle type' }]}
						>
							<Select
								placeholder="Select"
								showSearch
								filterOption={(input, option) =>
									(option?.children ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								{vehicleTypeOptions.map((type) => (
									<Option key={type.value} value={type.value}>
										{type.label}
									</Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							name="insurance_key"
							label="Insurance Doc Upload"
							rules={[
								{
									required: !skipDocumentUpload,
									message: 'Please upload insurance document',
								},
							]}
						>
							<InlineRow>
								<UploadButtonWrapper>
									<PrimaryBrowseFileButton
										icon={<UploadOutlined />}
										onClick={() =>
											handleBrowseFile('insurance', insuranceDocFileInputRef)
										}
										loading={uploading.insurance}
										disabled={skipDocumentUpload}
									>
										Browse File
									</PrimaryBrowseFileButton>
									{fileNames.insurance && (
										<FileDisplayContainer>
											<FileInfo>
												<FileIcon />
												<Text style={{ fontSize: 14, color: '#374151' }}>
													Fileupload.Pdf{' '}
													{fileSizes.insurance &&
														formatFileSize(fileSizes.insurance)}
												</Text>
												{fileKeys.insurance_key && (
													<EyeOutlined
														style={{
															fontSize: 16,
															color: '#3b82f6',
															cursor: 'pointer',
															marginLeft: 8,
														}}
														onClick={() =>
															handleViewDocument(
																fileKeys.insurance_key,
																'Insurance Document'
															)
														}
													/>
												)}
											</FileInfo>
											<RemoveFileButton
												onClick={() => handleRemoveFile('insurance')}
											>
												<CloseOutlined />
											</RemoveFileButton>
										</FileDisplayContainer>
									)}
									<HiddenFileInput
										type="file"
										ref={insuranceDocFileInputRef}
										accept=".pdf,.png,.jpg,.jpeg"
										onChange={(e) => handleFileChange(e, 'insurance')}
									/>
								</UploadButtonWrapper>
								<DatePickerWrapper>
									<Form.Item
										name="insurance_validity_date"
										label="Valid Till"
										rules={[
											{
												required: !skipDocumentUpload,
												message: VALID_TILL_DATE_MESSAGE,
											},
										]}
										style={{ marginBottom: 0 }}
									>
										<DatePicker
											style={{ width: '100%' }}
											placeholder="Eg : 12-2-2025"
											format="DD-MM-YYYY"
											disabled={skipDocumentUpload}
										/>
									</Form.Item>
								</DatePickerWrapper>
							</InlineRow>
						</Form.Item>

						{/* Row 3: Vehicle RC Doc Upload + Valid Till | PUC Doc Upload + Valid Till */}
						<Form.Item
							name="rc_doc_key"
							label="Vehicle RC Doc Upload"
							rules={[
								{
									required: !skipDocumentUpload,
									message: 'Please upload RC document',
								},
							]}
						>
							<InlineRow>
								<UploadButtonWrapper>
									<PrimaryBrowseFileButton
										icon={<UploadOutlined />}
										onClick={() =>
											handleBrowseFile('rc_doc', rcDocFileInputRef)
										}
										loading={uploading.rc_doc}
										disabled={skipDocumentUpload}
									>
										Browse File
									</PrimaryBrowseFileButton>
									{fileNames.rc_doc && (
										<FileDisplayContainer>
											<FileInfo>
												<FileIcon />
												<Text style={{ fontSize: 14, color: '#374151' }}>
													Fileupload.Pdf{' '}
													{fileSizes.rc_doc &&
														formatFileSize(fileSizes.rc_doc)}
												</Text>
												{fileKeys.rc_doc_key && (
													<EyeOutlined
														style={{
															fontSize: 16,
															color: '#3b82f6',
															cursor: 'pointer',
															marginLeft: 8,
														}}
														onClick={() =>
															handleViewDocument(
																fileKeys.rc_doc_key,
																'RC Document'
															)
														}
													/>
												)}
											</FileInfo>
											<RemoveFileButton
												onClick={() => handleRemoveFile('rc_doc')}
											>
												<CloseOutlined />
											</RemoveFileButton>
										</FileDisplayContainer>
									)}
									<HiddenFileInput
										type="file"
										ref={rcDocFileInputRef}
										accept=".pdf,.png,.jpg,.jpeg"
										onChange={(e) => handleFileChange(e, 'rc_doc')}
									/>
								</UploadButtonWrapper>
								<DatePickerWrapper>
									<Form.Item
										name="rc_expiry_date"
										label="Valid Till"
										rules={[
											{
												required: !skipDocumentUpload,
												message: VALID_TILL_DATE_MESSAGE,
											},
										]}
										style={{ marginBottom: 0 }}
									>
										<DatePicker
											style={{ width: '100%' }}
											placeholder="Eg : 12-2-2025"
											format="DD-MM-YYYY"
											disabled={skipDocumentUpload}
										/>
									</Form.Item>
								</DatePickerWrapper>
							</InlineRow>
						</Form.Item>
						<Form.Item name="puc_doc_key" label="PUC Doc Upload">
							<InlineRow>
								<UploadButtonWrapper>
									<PrimaryBrowseFileButton
										icon={<UploadOutlined />}
										onClick={() =>
											handleBrowseFile('puc_doc', pucDocFileInputRef)
										}
										loading={uploading.puc_doc}
										disabled={skipDocumentUpload}
									>
										Browse File
									</PrimaryBrowseFileButton>
									{fileNames.puc_doc && (
										<FileDisplayContainer>
											<FileInfo>
												<FileIcon />
												<Text style={{ fontSize: 14, color: '#374151' }}>
													Fileupload.Pdf{' '}
													{fileSizes.puc_doc &&
														formatFileSize(fileSizes.puc_doc)}
												</Text>
												{fileKeys.puc_doc_key && (
													<EyeOutlined
														style={{
															fontSize: 16,
															color: '#3b82f6',
															cursor: 'pointer',
															marginLeft: 8,
														}}
														onClick={() =>
															handleViewDocument(
																fileKeys.puc_doc_key,
																'PUC Document'
															)
														}
													/>
												)}
											</FileInfo>
											<RemoveFileButton
												onClick={() => handleRemoveFile('puc_doc')}
											>
												<CloseOutlined />
											</RemoveFileButton>
										</FileDisplayContainer>
									)}
									<HiddenFileInput
										type="file"
										ref={pucDocFileInputRef}
										accept=".pdf,.png,.jpg,.jpeg"
										onChange={(e) => handleFileChange(e, 'puc_doc')}
									/>
								</UploadButtonWrapper>
								<DatePickerWrapper>
									<Form.Item
										name="puc_expiry_date"
										label="Valid Till"
										required={isPucExpiryDateRequired}
										rules={[
											{
												validator: (_, value) => {
													if (!isPucExpiryDateRequired || value) {
														return Promise.resolve();
													}

													return Promise.reject(
														new Error(VALID_TILL_DATE_MESSAGE)
													);
												},
											},
										]}
										style={{ marginBottom: 0 }}
									>
										<DatePicker
											style={{ width: '100%' }}
											placeholder="Eg : 12-2-2025"
											format="DD-MM-YYYY"
											disabled={skipDocumentUpload}
										/>
									</Form.Item>
								</DatePickerWrapper>
							</InlineRow>
						</Form.Item>

						{/* Row 4: Owner Ship Type | Upload File (consent letter) */}
						<Form.Item name="ownership_type" label="Owner Ship Type">
							<Select placeholder="Select" disabled={skipDocumentUpload}>
								{OWNERSHIP_TYPE_OPTIONS.map((option) => (
									<Option key={option.value} value={option.value}>
										{option.label}
									</Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							name="consent_letter_key"
							label="Upload File"
							required={ownershipType === 'Aggregator' && !skipDocumentUpload}
							rules={[
								{
									validator: (_, value) => {
										const ownershipTypeValue =
											form.getFieldValue('ownership_type');
										if (
											ownershipTypeValue === 'Aggregator' &&
											!value &&
											!skipDocumentUpload
										) {
											return Promise.reject(
												new Error(
													'Please upload consent letter for Aggregator'
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<InlineRow>
								<Input
									placeholder="Eg : File"
									value={fileNames.consent_letter || ''}
									disabled
									readOnly
									style={{ backgroundColor: '#f9fafb', flex: 1 }}
								/>
								<PrimaryBrowseFileButton
									icon={<UploadOutlined />}
									onClick={() =>
										handleBrowseFile('consent_letter', consentFileInputRef)
									}
									loading={uploading.consent_letter}
									disabled={!ownershipType || skipDocumentUpload}
								>
									Browse File
								</PrimaryBrowseFileButton>
							</InlineRow>
							{fileNames.consent_letter && (
								<FileDisplayContainer style={{ marginTop: 8 }}>
									<FileInfo>
										<FileIcon />
										<Text style={{ fontSize: 14, color: '#374151' }}>
											Fileupload.Pdf{' '}
											{fileSizes.consent_letter &&
												formatFileSize(fileSizes.consent_letter)}
										</Text>
										{fileKeys.consent_letter_key && (
											<EyeOutlined
												style={{
													fontSize: 16,
													color: '#3b82f6',
													cursor: 'pointer',
													marginLeft: 8,
												}}
												onClick={() =>
													handleViewDocument(
														fileKeys.consent_letter_key,
														'Consent Letter'
													)
												}
											/>
										)}
									</FileInfo>
									<RemoveFileButton
										onClick={() => handleRemoveFile('consent_letter')}
									>
										<CloseOutlined />
									</RemoveFileButton>
								</FileDisplayContainer>
							)}
							<HiddenFileInput
								type="file"
								ref={consentFileInputRef}
								accept=".pdf,.png,.jpg,.jpeg"
								onChange={(e) => handleFileChange(e, 'consent_letter')}
							/>
						</Form.Item>
						{/* Row 5: GPS Installed | GPS Provider Name */}
						<Form.Item
							name="gps_installed"
							label="GPS Installed"
							rules={[
								{
									required: !skipDocumentUpload,
									message: 'Please select GPS installation status',
								},
							]}
						>
							<Select placeholder="Select" disabled={skipDocumentUpload}>
								<Option value>Yes</Option>
								<Option value={false}>No</Option>
							</Select>
						</Form.Item>
						{gpsInstalled === true && !skipDocumentUpload && (
							<Form.Item
								name="gps_provider_name"
								label="GPS Provider Name"
								rules={[
									{
										required: true,
										message: 'Please enter GPS provider name',
									},
								]}
							>
								<Input placeholder="Enter Provider Name" />
							</Form.Item>
						)}
						<Form.Item label="Skip Document Upload">
							<SkipDocumentUploadRow>
								<SkipDocumentCheckbox
									checked={skipDocumentUpload}
									disabled={isSkipDocumentUploadLocked}
									onChange={(e) => {
										setSkipDocumentUpload(e.target.checked);
										if (e.target.checked) {
											clearUploadedDocumentState();
										}
									}}
								>
									SKIP DOCUMENT UPLOAD
								</SkipDocumentCheckbox>
								<Tooltip
									title="A vehicle can be onboarded by skipping the documents for now.But a trip cannot be closed if the vehicle documents are not present."
									placement="right"
								>
									<SkipDocumentInfoIcon>i</SkipDocumentInfoIcon>
								</Tooltip>
							</SkipDocumentUploadRow>
						</Form.Item>
					</FormGrid>
				</StyledForm>
			</StyledModal>

			{/* Document Viewer Modal */}
			<DocumentViewerModal
				visible={documentViewerVisible}
				onClose={handleCloseDocumentViewer}
				documentUrl={viewingDocumentUrl}
				documentName={viewingDocumentName}
				documentType={viewingDocumentType}
			/>
		</>
	);
}

EditVehicleModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	vehicle: PropTypes.object,
	vendorOptions: PropTypes.array.isRequired,
	vehicleTypeOptions: PropTypes.array.isRequired,
	onClose: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};

EditVehicleModal.defaultProps = {
	vehicle: null,
};

export default EditVehicleModal;
